import React, { ReactNode, useRef, useState } from 'react'
import { Divider, Grid, Icon, Text, Tooltip, usePercent } from '@afterpaytouch/core'
import styles from './style.module.css'
import {
  useAppRouter,
  useCalculatedPaymentSchedules,
  useCheckoutState,
  useSUPFinanceFee,
  useIsAfterpayAppGiftCardFlow,
  useIsAfterpayFinanceFeeEligible,
  useGiftCardPromoBreakdown,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { Money } from '../Money'
import { GiftCardPromoBreakdown } from '../GiftCardPromoBreakdown'
import { useTranslation } from '../../utils/convergence'

const GridItem = Grid.Item
const GridFixedItem = Grid.FixedItem

const FINANCE_FEE_TOOLTIP_ID = 'estimated-order-breakdown-finance-fee-tooltip'

export const EstimatedOrderBreakdown = (): JSX.Element => {
  const { started } = useCheckoutState()
  const { locale } = useAppRouter()
  const { smallTextSize, textSize } = useCheckoutSizes()
  const [showFinanceFeeTooltip, setShowFinanceFeeTooltip] = useState(false)
  const financeFeeItemRef = useRef<HTMLButtonElement>(null)
  const paymentSchedules = useCalculatedPaymentSchedules()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const giftCardPromotion = useGiftCardPromoBreakdown()
  const { t } = useTranslation(['financeFee'])
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const estimatedCartAmount = started?.consumerAmount
  const estimatedOrderAmount = started?.consumerAmountWithFees
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const supFinanceFee = useSUPFinanceFee()
  const rate = formatPercent(supFinanceFee?.variableRate)

  const toggleFinanceFeeTooltip = (): void => {
    setShowFinanceFeeTooltip(!showFinanceFeeTooltip)
  }

  const tooltipContent = (): ReactNode => {
    return (
      <Text size={smallTextSize}>
        {' '}
        {isAfterpayAppGiftCardFlow ? t('summary.giftCard.financeFee.tooltip', { rate }) : t('summary.sup.financeFee.tooltip', { rate })}{' '}
      </Text>
    )
  }

  const renderFinanceFeeTooltip = (): ReactNode => {
    if (!showFinanceFeeTooltip) {
      return null
    }

    return (
      <Tooltip
        id={FINANCE_FEE_TOOLTIP_ID}
        triggerRef={financeFeeItemRef}
        caretPlacement='top-start'
        usePortal={false}
        content={tooltipContent()}
        skidding={65}
        hasCaret={true}
        useMobileView={false}
        bubbleSize={{ width: '100%' }}
        closeOnOutsideClick={true}
        onClose={toggleFinanceFeeTooltip}
      />
    )
  }

  const renderEstimatedOrderBreakdown = (): ReactNode => (
    <div className={styles.estimatedOrderBreakdownContainer}>
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' color='Standard' size={textSize}>
            {t('summary.sup.cartTotal.title')}
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          *<Money locale={locale} color='Standard' size={textSize} value={estimatedCartAmount} />
        </GridFixedItem>
      </Grid>
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' color='Standard' size={textSize}>
            {t('summary.sup.financeFee.title')}
            <button
              type='button'
              ref={financeFeeItemRef}
              className={styles.financeFeeTooltipIcon}
              onClick={toggleFinanceFeeTooltip}
              data-testid='finance-fee-tooltip-icon'
              aria-label={t('summary.sup.financeFee.info')}
              aria-describedby={showFinanceFeeTooltip ? FINANCE_FEE_TOOLTIP_ID : undefined}
              aria-expanded={showFinanceFeeTooltip}
            >
              <Icon.Info size='Small' color='Subtle' />
            </button>
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          <Money locale={locale} color='Standard' size={textSize} value={supFinanceFee?.variableAmount} />
        </GridFixedItem>
      </Grid>
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' color='Standard' size={textSize}>
            {t('summary.sup.orderTotal.title')}
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          <Money locale={locale} color='Standard' size={textSize} value={estimatedOrderAmount} />
        </GridFixedItem>
      </Grid>
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' color='Standard' size={textSize} bold>
            {t('orders:orderSummary.downPaymentDueToday', { total: paymentSchedules.length })}
          </Text>{' '}
          <div className={styles.wrappedOneOfX}>
            <Text renderAs='span' color='Standard' size={textSize} bold>
              {t('orders:orderSummary.oneOfX', { total: paymentSchedules.length })}
            </Text>
          </div>
        </GridItem>
        <GridFixedItem align='right'>
          <Money bold locale={locale} size={textSize} color='Standard' value={paymentSchedules[0].amount} />
        </GridFixedItem>
      </Grid>
    </div>
  )

  const renderSUPTerms = (): ReactNode => {
    if (!isAFF || isAfterpayAppGiftCardFlow) {
      return null
    }

    return (
      <div className={styles.termsContainer}>
        <Text color='Subtle' size={smallTextSize} testNameSpace='estimated-order-breakdown-terms'>
          {t('summary.estimatedTerms')}
        </Text>
      </div>
    )
  }

  const renderGiftCardOrderBreakdown = (): ReactNode => (
    <div className={styles.estimatedOrderBreakdownContainer}>
      {giftCardPromotion ? (
        <GiftCardPromoBreakdown promotion={giftCardPromotion} />
      ) : (
        <Grid justifyContent='space-between'>
          <GridItem>
            <Text renderAs='span' color='Standard' size={textSize}>
              {t('summary.giftCard.purchaseAmount.title')}
            </Text>
          </GridItem>
          <GridFixedItem align='right'>
            <Money locale={locale} color='Standard' size={textSize} value={estimatedCartAmount} />
          </GridFixedItem>
        </Grid>
      )}
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' color='Standard' size={textSize}>
            {t('summary.giftCard.financeFee.title')}
            <button
              type='button'
              ref={financeFeeItemRef}
              className={styles.financeFeeTooltipIcon}
              onClick={toggleFinanceFeeTooltip}
              data-testid='finance-fee-tooltip-icon'
              aria-label={t('summary.giftCard.financeFee.info')}
              aria-describedby={showFinanceFeeTooltip ? FINANCE_FEE_TOOLTIP_ID : undefined}
              aria-expanded={showFinanceFeeTooltip}
            >
              <Icon.Info size='Small' color='Subtle' />
            </button>
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          <Money locale={locale} color='Standard' size={textSize} value={supFinanceFee?.variableAmount} />
        </GridFixedItem>
      </Grid>
      <div className={styles.dividerContainer}>
        <Divider kind='Hairline' />
      </div>
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' color='Standard' size={textSize}>
            {t('summary.giftCard.totalAmount.title')}
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          <Money locale={locale} color='Standard' size={textSize} value={estimatedOrderAmount} />
        </GridFixedItem>
      </Grid>
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' color='Standard' size={textSize} bold>
            {t('orders:orderSummary.downPaymentDueToday', { total: paymentSchedules.length })}
          </Text>{' '}
          <div className={styles.wrappedOneOfX}>
            <Text renderAs='span' color='Standard' size={textSize} bold>
              {t('orders:orderSummary.oneOfX', { total: paymentSchedules.length })}
            </Text>
          </div>
        </GridItem>
        <GridFixedItem align='right'>
          <Money bold locale={locale} size={textSize} color='Standard' value={paymentSchedules[0].amount} />
        </GridFixedItem>
      </Grid>
    </div>
  )

  const renderBreakdown = (): ReactNode => {
    if (isAfterpayAppGiftCardFlow) {
      return renderGiftCardOrderBreakdown()
    }
    return renderEstimatedOrderBreakdown()
  }

  return (
    <div className={styles.outerContainer} data-testid='estimated-order-breakdown'>
      {renderFinanceFeeTooltip()}
      {renderBreakdown()}
      {renderSUPTerms()}
      <div className={styles.dividerContainer}>
        <Divider kind='Hairline' />
      </div>
    </div>
  )
}
